import { SupportTicketSnoozeDuration } from "@redotech/cockroachdb-codegen/tables/shopper-ai/support-ticket-snooze-duration";
import { Schema } from "effect";
import {
  AccountId,
  ChannelThreadId,
  ShopperAiIntegrationId,
  ShopperAiTagId,
  SupportTicketId,
} from "../../../ids/definitions";
import {
  MongoCustomerId,
  MongoTeamId,
  MongoUserId,
} from "../../../ids/mongo-ids";
import { CustomerBasicInfo } from "../../../models/customer";
import { NullableDateFromDb } from "../../../schema/helpers";
import { DraftMessageStatus } from "./draft-message-status";
import { MessageChannel } from "./message";
import { Attachment } from "./message/attachment";
import { ChannelThread } from "./thread";

export const SUPPORT_TICKET_STATUSES = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
  "DRAFT",
] as const;
export const SupportTicketStatus = Schema.Literal(...SUPPORT_TICKET_STATUSES);
export type SupportTicketStatus = typeof SupportTicketStatus.Type;
const SUPPORT_TICKET_STATUS_SET: ReadonlySet<string> = new Set(
  SUPPORT_TICKET_STATUSES,
);

export function isSupportTicketStatus(
  value: string,
): value is SupportTicketStatus {
  return SUPPORT_TICKET_STATUS_SET.has(value);
}

export const TERMINAL_SUPPORT_TICKET_STATUSES = [
  "CLOSED",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const satisfies readonly SupportTicketStatus[];
const TERMINAL_SUPPORT_TICKET_STATUS_SET: ReadonlySet<SupportTicketStatus> =
  new Set(TERMINAL_SUPPORT_TICKET_STATUSES);

export function isTerminalSupportTicketStatus(
  status: SupportTicketStatus,
): boolean {
  return TERMINAL_SUPPORT_TICKET_STATUS_SET.has(status);
}

export const SupportTicketPriority = Schema.Literal("URGENT");
export type SupportTicketPriority = typeof SupportTicketPriority.Type;
// String-literal source of truth so downstream Schema.Literal / Zod consumers
// type-check against `"1h" | "4h" | "24h"` rather than the nominal
// SupportTicketSnoozeDuration enum (which TS won't accept from raw string
// literals). The `satisfies` clause ties this list to the codegen enum so it
// fails to compile if either set acquires a value the other doesn't.
// The literal must stay on one line so the //redontIgnore below applies
// under the current ReDONT rule (multiline-literal support lands in #47924).
// prettier-ignore
//redontIgnore
export const FIXED_SUPPORT_TICKET_SNOOZE_OPTIONS = ["1h", "4h", "24h"] as const satisfies readonly `${SupportTicketSnoozeDuration}`[];
export const SupportTicketSnoozeOptions = Schema.Literal(
  ...FIXED_SUPPORT_TICKET_SNOOZE_OPTIONS,
  "custom",
);
export type SupportTicketSnoozeOptions = typeof SupportTicketSnoozeOptions.Type;
export type FixedSupportTicketSnoozeOption = Exclude<
  SupportTicketSnoozeOptions,
  "custom"
>;

export function computeSupportTicketSnoozeUntil(
  option: FixedSupportTicketSnoozeOption,
  now = Date.now(),
): Date {
  switch (option) {
    case "1h":
      return new Date(now + 60 * 60 * 1000);
    case "4h":
      return new Date(now + 4 * 60 * 60 * 1000);
    case "24h":
      return new Date(now + 24 * 60 * 60 * 1000);
  }
}

export const SUPPORT_TICKET_ASSIGNEE_TYPES = [
  "USER",
  "AI",
  "CHAT_FLOW",
] as const;
export const SupportTicketAssigneeType = Schema.Literal(
  ...SUPPORT_TICKET_ASSIGNEE_TYPES,
);
export type SupportTicketAssigneeType = typeof SupportTicketAssigneeType.Type;

export const SUPPORT_TICKET_CLOSURE_REASONS = [
  "EXPIRED",
  "RESOLVED",
  "MANUAL",
  "SPAM",
  "DUPLICATE",
] as const;
export const SupportTicketClosureReason = Schema.Literal(
  ...SUPPORT_TICKET_CLOSURE_REASONS,
);
export type SupportTicketClosureReason = typeof SupportTicketClosureReason.Type;

export const CustomerSentiment = Schema.Literal(
  "POSITIVE",
  "NEUTRAL",
  "NEGATIVE",
);
export type CustomerSentiment = typeof CustomerSentiment.Type;

export const AssigneeSummary = Schema.Literal(
  "AI_AGENT_HANDLED",
  "MERCHANT_HANDLED",
  "AI_AGENT_HANDOFF_TO_HUMAN",
  "AI_AGENT_HANDOFF_TO_IGNORED",
  "MERCHANT_IGNORED",
);
export type AssigneeSummary = typeof AssigneeSummary.Type;

export const SupportTicketResponse = Schema.Struct({
  id: SupportTicketId,
  accountId: AccountId,
  status: SupportTicketStatus,
  priority: Schema.optional(SupportTicketPriority),
  mongoCustomerId: Schema.NullOr(MongoCustomerId),
  mongoAssigneeId: Schema.NullOr(MongoUserId),
  ticketNumber: Schema.Number,
});

export type SupportTicketResponse = typeof SupportTicketResponse.Type;

export const SUPPORT_TICKET_SEARCH_HIGHLIGHT_PRE_TAG =
  "__REDO_SUPPORT_HIGHLIGHT_START__";
export const SUPPORT_TICKET_SEARCH_HIGHLIGHT_POST_TAG =
  "__REDO_SUPPORT_HIGHLIGHT_END__";

export const SupportTicketSearchHighlightField = Schema.Literal(
  "summary",
  "messageContent",
  "emailSubject",
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
);
export type SupportTicketSearchHighlightField =
  typeof SupportTicketSearchHighlightField.Type;

export const SupportTicketSearchHighlight = Schema.Struct({
  field: SupportTicketSearchHighlightField,
  fragments: Schema.Array(Schema.String),
});
export type SupportTicketSearchHighlight =
  typeof SupportTicketSearchHighlight.Type;

export const supportTicketListCountFields = [
  "numDrafts",
  "numSentMessages",
  "numOutboundMessages",
  "numCustomerMessages",
  "numMerchantSentMessages",
  "numAiSentMessages",
  "numAutoReplySentMessages",
  "numSystemSentMessages",
  "numInternalNotes",
] as const;

export const emptySupportTicketListCountFields = {
  numDrafts: 0,
  numSentMessages: 0,
  numOutboundMessages: 0,
  numCustomerMessages: 0,
  numMerchantSentMessages: 0,
  numAiSentMessages: 0,
  numAutoReplySentMessages: 0,
  numSystemSentMessages: 0,
  numInternalNotes: 0,
} satisfies Record<(typeof supportTicketListCountFields)[number], number>;

export const SupportTicketListItemResponse = Schema.Struct({
  id: SupportTicketId,
  accountId: AccountId,
  teamId: Schema.optional(MongoTeamId),
  status: SupportTicketStatus,
  priority: Schema.NullOr(SupportTicketPriority),
  mongoCustomerId: Schema.NullOr(MongoCustomerId),
  mongoAssigneeId: Schema.NullOr(MongoUserId),
  assigneeType: Schema.NullOr(SupportTicketAssigneeType),
  readAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  summary: Schema.NullOr(Schema.String),
  lastMessagePreview: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
  /**
   * Latest unsent merchant draft content (plain text) for the ticket, or null
   * when there is no draft on any thread.
   */
  draftPreview: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
  /**
   * Status of the latest merchant draft. Null when there is no draft.
   * "ERROR" means the last send attempt failed and the draft remains unsent.
   */
  draftStatus: Schema.optionalWith(Schema.NullOr(DraftMessageStatus), {
    default: () => null,
  }),
  emailSubject: Schema.NullOr(Schema.String),
  customerSentiment: Schema.NullOr(CustomerSentiment),
  assigneeSummary: Schema.NullOr(AssigneeSummary),
  channels: Schema.Array(MessageChannel),
  customer: Schema.optional(CustomerBasicInfo),
  tagIds: Schema.Array(ShopperAiTagId),
  createdAt: Schema.NullOr(Schema.Date),
  statusUpdatedAt: Schema.NullOr(Schema.Date),
  snoozedUntil: Schema.NullOr(Schema.Date),
  ticketNumber: Schema.NullOr(Schema.Number),
  /**
   * Reason the ticket was closed. 'DUPLICATE' indicates the ticket was merged
   * into another ticket as a source. Null for open/snoozed/in-progress tickets.
   */
  closureReason: Schema.optionalWith(
    Schema.NullOr(SupportTicketClosureReason),
    { default: () => null },
  ),
  /**
   * Count of unsent draft messages for this support ticket.
   * Populated from OpenSearch index for filtered list queries.
   */
  numDrafts: Schema.Number,
  /**
   * Count of sent merchant messages for this support ticket.
   * Populated from OpenSearch index for filtered list queries.
   */
  numSentMessages: Schema.Number,
  numOutboundMessages: Schema.Number,
  numCustomerMessages: Schema.Number,
  numMerchantSentMessages: Schema.Number,
  numAiSentMessages: Schema.Number,
  numAutoReplySentMessages: Schema.Number,
  numSystemSentMessages: Schema.Number,
  numInternalNotes: Schema.Number,
  searchHighlights: Schema.optionalWith(
    Schema.Array(SupportTicketSearchHighlight),
    { default: () => [] },
  ),
});
export type SupportTicketListItemResponse =
  typeof SupportTicketListItemResponse.Type;

/**
 * Type guard for SupportTicketListItemResponse.
 * Validates all required fields with their expected types.
 */
export function isSupportTicketListItem(
  context: unknown,
): context is SupportTicketListItemResponse {
  if (context === null || typeof context !== "object") {
    return false;
  }

  const obj = context as Record<string, unknown>;

  // Check required string fields
  if (
    typeof obj.id !== "string" ||
    typeof obj.accountId !== "string" ||
    (obj.mongoCustomerId !== null && typeof obj.mongoCustomerId !== "string")
  ) {
    return false;
  }

  // Check status is a valid support ticket status
  if (
    typeof obj.status !== "string" ||
    !SUPPORT_TICKET_STATUSES.includes(obj.status as SupportTicketStatus)
  ) {
    return false;
  }

  // Check nullable date fields
  const isNullOrDate = (val: unknown): boolean =>
    val === null || val instanceof Date;

  if (
    !isNullOrDate(obj.readAt) ||
    !isNullOrDate(obj.lastCustomerMessageAt) ||
    !isNullOrDate(obj.lastMerchantMessageAt)
  ) {
    return false;
  }

  // Check nullable string fields
  const isNullOrString = (val: unknown): boolean =>
    val === null || typeof val === "string";

  if (!isNullOrString(obj.summary)) {
    return false;
  }

  // Check tagIds is an array
  if (!Array.isArray(obj.tagIds)) {
    return false;
  }

  return true;
}

export const SupportTicketListCursor = Schema.Struct({
  lastCustomerMessageAt: Schema.NullOr(Schema.DateFromString),
  id: SupportTicketId,
});
export type SupportTicketListCursor = typeof SupportTicketListCursor.Type;

export const UserTicketStats = Schema.Struct({
  mongoAssigneeId: MongoUserId.fromDbBuffer,
  openTicketCount: Schema.NumberFromString.pipe(
    Schema.int(),
    Schema.nonNegative(),
  ),
  mostRecentTicketDate: NullableDateFromDb,
});
export type UserTicketStats = typeof UserTicketStats.Type;

/**
 * Discriminated union for tag update operations on a support ticket.
 * - "replace": Full replacement of all tags (deletes unspecified, upserts specified)
 * - "add": Additive — only upserts the given tags, leaves others untouched
 * - "remove": Subtractive — only removes the given tags, leaves others untouched
 */
export const ConversationTagUpdate = Schema.Union(
  Schema.Struct({
    _tag: Schema.Literal("replace"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
  Schema.Struct({
    _tag: Schema.Literal("add"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
  Schema.Struct({
    _tag: Schema.Literal("remove"),
    tagIds: Schema.Array(ShopperAiTagId),
  }),
);
export type ConversationTagUpdate = typeof ConversationTagUpdate.Type;

/**
 * Discriminated union for assigning a support ticket. The backend resolves
 * user IDs for non-user types (e.g. AI_AUTOPILOT_USER_ID for "ai").
 */
export const SupportTicketAssignmentUpdate = Schema.Union(
  Schema.Struct({
    assignmentType: Schema.Literal("user"),
    userId: MongoUserId,
  }),
  Schema.Struct({ assignmentType: Schema.Literal("ai") }),
  Schema.Struct({ assignmentType: Schema.Literal("chatFlow") }),
  Schema.Struct({ assignmentType: Schema.Literal("unassign") }),
);
export type SupportTicketAssignmentUpdate =
  typeof SupportTicketAssignmentUpdate.Type;

export const UpdateSupportTicketInput = Schema.Struct({
  status: Schema.optional(SupportTicketStatus),
  // Recorded with a CLOSED status to capture why the ticket closed.
  closureReason: Schema.optional(SupportTicketClosureReason),
  priority: Schema.optional(Schema.OptionFromNullOr(SupportTicketPriority)),
  readAt: Schema.optional(Schema.OptionFromNullOr(Schema.Date)),
  mongoCustomerId: Schema.optional(Schema.OptionFromNullOr(MongoCustomerId)),
  assignment: Schema.optional(SupportTicketAssignmentUpdate),
  tagUpdate: Schema.optional(ConversationTagUpdate),
  snoozedUntil: Schema.optional(Schema.NullOr(Schema.Date)),
  // Cosmetic override of the email subject shown for this ticket. The original
  // inbound `subject` is preserved; this is written to the email thread's
  // `display_subject` and shown in its place. Applies to every email channel
  // (gmail/outlook/custom_email) since they share the channel_thread_email row.
  displaySubject: Schema.optional(Schema.String),
  // Forces the next AI turn to start a fresh thread instead of resuming the
  // prior escalated one. Set when re-handing a ticket back to AI.
  clearMongoLlmThreadId: Schema.optional(Schema.Boolean),
});
export type UpdateSupportTicketInput = typeof UpdateSupportTicketInput.Type;

export const EmailRecipientSchema = Schema.Struct({
  email: Schema.String.pipe(Schema.minLength(1)),
  name: Schema.NullOr(Schema.String),
});
export type EmailRecipientSchema = typeof EmailRecipientSchema.Type;

export const StartSupportTicketInput = Schema.Struct({
  integrationId: ShopperAiIntegrationId,
  customerEmail: Schema.String,
  subject: Schema.String,
  content: Schema.String,
  attachments: Schema.Array(Attachment),
  to: Schema.optional(Schema.Array(EmailRecipientSchema)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
});
export type StartSupportTicketInput = typeof StartSupportTicketInput.Type;

export const StartSupportTicketResponse = Schema.Struct({
  supportTicketId: SupportTicketId,
  workflowId: Schema.String,
});
export type StartSupportTicketResponse = typeof StartSupportTicketResponse.Type;

export const CreateDraftSupportTicketInput = Schema.Struct({
  integrationId: ShopperAiIntegrationId,
  customerEmail: Schema.optional(Schema.String),
  /** Customer phone number, used for telnyx_sms threads */
  customerPhone: Schema.optional(Schema.String),
  customerFirstName: Schema.optional(Schema.String),
  customerLastName: Schema.optional(Schema.String),
  subject: Schema.optional(Schema.String),
  /** If provided, a draft message will be saved for the new thread */
  draftContent: Schema.optional(Schema.String),
  attachments: Schema.optional(Schema.Array(Attachment)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
});
export type CreateDraftSupportTicketInput =
  typeof CreateDraftSupportTicketInput.Type;

export const CreateDraftSupportTicketResponse = Schema.Struct({
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
});
export type CreateDraftSupportTicketResponse =
  typeof CreateDraftSupportTicketResponse.Type;

// Input for creating a draft thread on an existing support ticket.
// Used when replying to a contact form submission with a different channel
// (email/sms). Supported integration types: gmail, outlook, telnyx_sms.
export const CreateDraftThreadInput = Schema.Struct({
  integrationId: ShopperAiIntegrationId,
  /** Customer email, used for gmail/outlook threads */
  customerEmail: Schema.optional(Schema.String),
  /** Customer phone, used for telnyx_sms threads */
  customerPhone: Schema.optional(Schema.String),
  subject: Schema.optional(Schema.String),
  draftContent: Schema.optional(Schema.String),
  attachments: Schema.optional(Schema.Array(Attachment)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
});
export type CreateDraftThreadInput = typeof CreateDraftThreadInput.Type;

export const CreateDraftThreadResponse = Schema.Struct({
  thread: ChannelThread,
});
export type CreateDraftThreadResponse = typeof CreateDraftThreadResponse.Type;

export const SupportTicketThreadContext = Schema.Union(
  Schema.Struct({
    _tag: Schema.Literal("mergedSource"),
    sourceSupportTicketId: SupportTicketId,
    sourceTicketNumber: Schema.NullOr(Schema.Number),
  }),
  Schema.Struct({
    _tag: Schema.Literal("contextOnly"),
    activeSupportTicketId: Schema.optional(SupportTicketId),
    activeSupportTicketNumber: Schema.optional(Schema.NullOr(Schema.Number)),
  }),
);
export type SupportTicketThreadContext = typeof SupportTicketThreadContext.Type;

export const SupportTicketThreadResponse = Schema.Struct({
  thread: ChannelThread,
  ticketContext: Schema.optional(SupportTicketThreadContext),
  messageCount: Schema.optional(Schema.Number),
});
export type SupportTicketThreadResponse =
  typeof SupportTicketThreadResponse.Type;

export const PublishDraftSupportTicketInput = Schema.Struct({
  /** Customer email address — required for email channel tickets */
  customerEmail: Schema.optional(Schema.String),
  /** Customer phone number — required for SMS channel tickets */
  customerPhone: Schema.optional(Schema.String),
  customerFirstName: Schema.optional(Schema.String),
  customerLastName: Schema.optional(Schema.String),
  subject: Schema.optional(Schema.String),
  content: Schema.String,
  attachments: Schema.Array(Attachment),
  to: Schema.optional(Schema.Array(EmailRecipientSchema)),
  cc: Schema.optional(Schema.Array(EmailRecipientSchema)),
  bcc: Schema.optional(Schema.Array(EmailRecipientSchema)),
});
export type PublishDraftSupportTicketInput =
  typeof PublishDraftSupportTicketInput.Type;
